import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { ScheduledPostsClient, ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { scheduledPostsKeys } from './scheduledPostsKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCurrentArticleId } from 'hooks/useCurrentArticleId'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { showToast } from 'common/ToastMessage'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ProcessingPostToast } from 'components/ArticlePostPreview/PostPreviewBody/PollScheduledPostStatus/PollScheduledPostStatus'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { updateCalendarItemInCache } from 'hooks/queries/calendar/utils'
import { updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { getEarliestPublishedAt } from './scheduledPostUtils'
import { formatPostError } from 'utils/formatPostError'
import dayjs from 'dayjs'

export const usePublishScheduledPost = ({
  onSuccess,
  onFailure,
}: {
  onSuccess?: () => void
  onFailure?: () => void
} = {}) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const articleId = useCurrentArticleId()

  // State to track the published post ID and posting state.
  const [publishedPostId, setPublishedPostId] = useState<number | null>(null)
  const [prevIsPosting, setPrevIsPosting] = useState(false)

  // Publish mutation.
  const mutation = useMutation(
    async ({ id }: { id: number }) => {
      const res = await ScheduledPostsClient.publish({
        workspaceId,
        scheduledPostId: id,
      })
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Error publishing scheduled post')
      }
      return res.data
    },
    {
      onError: (error: Error) => {
        showToastMessage(error.message, 'error')
      },
      onSettled: (_, __, variables) => {
        queryClient.invalidateQueries(scheduledPostsKeys.show(variables.id))
        if (articleId) {
          queryClient.invalidateQueries(scheduledPostsKeys.article(articleId))
        }
        queryClient.invalidateQueries(scheduledPostsKeys.index(workspaceId))
      },
    },
  )

  // When the publish mutation succeeds, set the published post ID
  // and show an immediate toast that the post is uploading.
  useEffect(() => {
    if (mutation.data?.scheduled_post?.id) {
      setPublishedPostId(mutation.data.scheduled_post.id)
      showToast(
        <ProcessingPostToast isPosting />,
        0,
        scheduledPostsKeys.show(mutation.data.scheduled_post.id).toString(),
      )
    }
  }, [mutation.data])

  // Hook to update post properties.
  const { updateStatus } = usePostProperties({
    id: mutation.data?.scheduled_post.almanac_file?.id,
    type: mutation.data?.scheduled_post.source_type as AssigneeType,
  })

  // Use the existing hook to poll for scheduled post status.
  // The query is disabled if publishedPostId is null.
  const { data: polledScheduledPost } = useScheduledPost(
    { id: publishedPostId ?? undefined },
    {
      refetchInterval: (data?: ScheduledPostShow) => {
        return data && data.is_posting ? 1000 : false
      },
    },
  )

  useEffect(() => {
    if (polledScheduledPost && publishedPostId !== null) {
      if (polledScheduledPost.is_posting && !prevIsPosting) {
        setPrevIsPosting(true)
        showToast(<ProcessingPostToast isPosting />, 0, scheduledPostsKeys.show(publishedPostId).toString())
      } else if (prevIsPosting && !polledScheduledPost.is_posting) {
        const toastKey = scheduledPostsKeys.show(publishedPostId).toString()
        if (polledScheduledPost.posting_failed) {
          const errorMessage = formatPostError(
            polledScheduledPost.error_type,
            polledScheduledPost.external_integration?.provider,
            polledScheduledPost.error_message,
          )
          showToast(<ProcessingPostToast postingFailed errorMessage={errorMessage} />, 5, toastKey)
          updateStatus?.(PostStatusPropertyValue.Failed)
          onFailure?.()
        } else {
          showToast(<ProcessingPostToast postUrl={polledScheduledPost.post_url} />, 5, toastKey)
          updateStatus?.(PostStatusPropertyValue.Posted)

          const postedAt = getEarliestPublishedAt(polledScheduledPost.scheduled_post_targets) ?? dayjs().toISOString()

          if (polledScheduledPost.almanac_file?.id) {
            updateCalendarItemInCache(
              queryClient,
              polledScheduledPost.almanac_file.id,
              (item) => ({
                ...item,
                post_at: postedAt,
                status: PostStatusPropertyValue.Posted,
                post_url: polledScheduledPost.post_url || '',
              }),
              workspaceId,
            )
            updateFileDetailCache<AlmanacFileShowItem>(
              queryClient,
              workspaceId,
              polledScheduledPost.almanac_file.id,
              (file) => ({ ...file, post_at: postedAt }),
            )
          }
          onSuccess?.()
        }
        setPrevIsPosting(false)
        // Stop polling by clearing the published post ID.
        setPublishedPostId(null)
      }
    }
  }, [
    polledScheduledPost,
    prevIsPosting,
    publishedPostId,
    updateStatus,
    onSuccess,
    onFailure,
    queryClient,
    workspaceId,
  ])

  return mutation
}
